export interface ValidGtmEventData {
  [key: string]: string | undefined;
}

/**
 * Dispatch custom events to Google Tag Manager
 * @param eventName Name of the event
 * @param data (Optional) include more data in the event
 */
export const gtmDispatchEvent = (eventName: string, data?: ValidGtmEventData) => {
  if (typeof window === "undefined") {
    return;
  }

  if (window["dataLayer"]) {
    window["dataLayer"].push({
      event: eventName,
      ...data
    });
  } else {
    window["dataLayer"] = [
      {
        event: eventName,
        ...data
      }
    ];
  }
};

// List of UTM and tracking parameter keys to capture
const UTM_PARAMETER_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_adgroup",
  "utm_term",
  "gclid",
  "creative",
  "device",
  "matchtype"
];

// Cookie name for storing the combined UTM parameters
const UTM_COOKIE_NAME = "persisted_utm_parameters";

/**
 * Flag to prevent clearing UTM parameters when navigating between pages
 * Will be set to true after first execution
 */
let hasLoaded = false;

/**
 * Captures UTM parameters from URL and stores them in a dedicated cookie
 * Should be called on each page load or route change
 */
export const captureUtmParameters = () => {
  if (typeof window === "undefined") {
    return;
  }

  // Get current parameters from URL
  const urlParams = new URLSearchParams(window.location.search);
  const currentParams = new URLSearchParams();

  // Flag to track if URL has any UTM parameters
  let hasUtmParams = false;

  // Extract only UTM parameters and tracking parameters
  UTM_PARAMETER_KEYS.forEach(key => {
    const value = urlParams.get(key);
    if (value) {
      currentParams.append(key, value);
      hasUtmParams = true;
    }
  });

  // Initial page load - if UTM params present in URL, save them
  // If not present during initial load, clear any existing params
  if (!hasLoaded) {
    hasLoaded = true;

    if (hasUtmParams) {
      // Store parameters in cookie
      document.cookie = `${UTM_COOKIE_NAME}=${currentParams.toString()}; path=/; SameSite=Lax`;
      return;
    }
  }

  // For subsequent navigation, only update if new UTM params are present
  if (hasUtmParams) {
    // Check if parameters differ from stored values
    const storedParamsString = getUtmParametersString();
    if (storedParamsString) {
      const normalizeParams = paramsString => {
        const params = new URLSearchParams(paramsString);
        const entries = Array.from(params.entries()).sort(([keyA], [keyB]) =>
          keyA.localeCompare(keyB)
        );

        const sortedParams = new URLSearchParams();
        entries.forEach(([key, value]) => sortedParams.append(key, value));

        return sortedParams.toString();
      };

      const normalizedCurrent = normalizeParams(currentParams.toString());
      const normalizedStored = normalizeParams(storedParamsString);

      if (normalizedCurrent === normalizedStored) {
        return;
      }
    }

    // Store parameters in cookie
    document.cookie = `${UTM_COOKIE_NAME}=${currentParams.toString()}; path=/; SameSite=Lax`;
  }
};

/**
 * Retrieves stored UTM parameters string from cookie
 * @returns UTM parameters string or empty string if not found
 */
export const getUtmParametersString = (): string => {
  if (typeof window === "undefined") {
    return "";
  }

  const cookieName = `${UTM_COOKIE_NAME}=`;
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length);
    }
  }

  return "";
};

